<script setup lang="ts">
import CircleCloseIcon from '@/assets/icons/shared/circle/close.svg';
import WarningIcon from '@/assets/icons/ui/warning.svg';
import CircleInfoIcon from '@/assets/icons/shared/circle/info.svg';
import { computed, useSlots } from 'vue';

export type UiMessageType = 'info' | 'warning' | 'error';

const { type } = defineProps<{
  type: UiMessageType;
}>();

defineSlots<{
  default: () => unknown;
  actions?: () => unknown;
}>();

const slots = useSlots();

const icon = computed(() => {
  switch (type) {
    case 'error':
      return CircleCloseIcon;
    case 'warning':
      return WarningIcon;
    case 'info':
      return CircleInfoIcon;
    default:
      throw new Error(`An unexpected type (${type}) was received in the UiMessage component.`);
  }
});
</script>

<template>
  <div
    class="ui-message"
    :class="`_type-${type}`"
  >
    <component
      :is="icon"
      class="icon"
    />

    <p class="text">
      <slot />
    </p>

    <div
      v-if="slots.actions"
      class="actions"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-message {
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  font-weight: var(--font-weight-bold);
  border-radius: 8px;

  &._type-info {
    background-color: var(--color-blue-100);
    color: var(--color-blue-800);

    .icon {
      fill: var(--color-blue-800);
    }
  }

  &._type-warning {
    background-color: var(--color-yellow-100);
    color: var(--color-yellow-800);

    .icon {
      fill: var(--color-yellow-800);
    }
  }

  &._type-error {
    background-color: var(--color-red-100);
    color: var(--color-red-1000);

    .icon {
      fill: var(--color-red-1000);
    }
  }
}

.icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.text {
  flex: 1;
  min-width: 0;
}

.actions {
  flex-shrink: 0;
}
</style>
